// libs
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

// helpers
import { isDateValid } from 'shared/helpers/utils';

// hooks
import {
  useSelectedSKU,
  useSelectedSKUDiscount,
  useSelectedSKUDiscountPrice,
  useIsSelectedSKUOOS,
  useSelectedSKUPrice,
  useSelectedSKUSubTitle,
  useSelectedSKUTitle,
  useSelectedSKUPriceRevealData,
  useSelectedSKUSalePriceLiveConfig,
  useSelectedSKUSaleParticipating,
  useSelectedSKUSpecialTag,
} from 'shared/store/details/hooks/selectors';
import { useSetSelectedSKU } from 'shared/store/details/hooks/actions';

//selectors
import { isOneSize, getFomoTag, getBrandLaunchMetadata, getBrandLaunchingSoon } from 'shared/store/details/selectors';
import { useDwebCartContext } from 'shared/components/DwebCart/context';

export const useProductInfo = () => {
  const [sizesAdded, setSizesAdded] = useState<string[]>([]);
  const { canShowCart } = useDwebCartContext();

  const location = useLocation();
  const setSelectedSKU = useSetSelectedSKU();
  const oneSize = useSelector(isOneSize);

  useEffect(() => {
    if (canShowCart) setSizesAdded([]);
  }, [canShowCart]);

  const cachedSkuId = useRef<string | null>(null);

  const setSelectedSize = useCallback(
    (size: ProductDetail.ISizeOption | undefined) => {
      if (!size || cachedSkuId.current === size.id) {
        return;
      }

      cachedSkuId.current = size.id;

      const { pathname, search } = location;
      const searchParams = new URLSearchParams(search);
      searchParams.set('skuId', size.id);

      const newURL = `${pathname}?${searchParams.toString()}`;

      // Directly manipulate the DOM to update the URL without triggering history.replace
      window && window.history.replaceState({}, '', newURL);
      setSelectedSKU({ skuId: size.id, oneSize: oneSize });
    },
    [location, oneSize, setSelectedSKU]
  );

  const selectedSize = useSelectedSKU();

  const addToCart = useCallback(() => {
    if (!selectedSize) return;

    if (!sizesAdded.includes(selectedSize.sizeName)) {
      setSizesAdded((prevSizes) => [...prevSizes, selectedSize.sizeName]);
    }
  }, [selectedSize, sizesAdded]);

  const isOutOfStock = useIsSelectedSKUOOS();
  const displayTitle = useSelectedSKUTitle();
  const displaySubTitle = useSelectedSKUSubTitle();
  const displayPrice = useSelectedSKUPrice();
  const displayDiscount = useSelectedSKUDiscount();
  const displayDiscountedPrice = useSelectedSKUDiscountPrice();
  const priceRevealData = useSelectedSKUPriceRevealData();
  const isFomoTimerValid = isDateValid(useSelector(getFomoTag)?.end_time) || false;
  const isSalePriceLive = useSelectedSKUSalePriceLiveConfig();
  const isSaleParticipating = useSelectedSKUSaleParticipating();
  const specialTag = useSelectedSKUSpecialTag();
  const brandLaunchMetadata = useSelector(getBrandLaunchMetadata);
  const brandLaunchingSoon = useSelector(getBrandLaunchingSoon);

  const isAddedToCart = selectedSize ? sizesAdded.includes(selectedSize.sizeName) : false;

  return {
    isAddedToCart,
    isOutOfStock,
    displayTitle,
    displaySubTitle,
    displayPrice,
    displayDiscount,
    displayDiscountedPrice,
    selectedSize,
    setSelectedSize,
    addToCart,
    priceRevealData,
    isOneSize: oneSize,
    isFomoTimerVisible: isFomoTimerValid,
    isSalePriceLive,
    isSaleParticipating,
    specialTag,
    brandLaunchMetadata,
    brandLaunchingSoon,
  };
};
